import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Toggle } from "@/components/ui/toggle";
import { useOnboarding } from "@/context/OnboardingContext";
import useOnboardingSetupAndInitialization from "@/hooks/useOnboardingSetupAndInitialization";
import useUserInteractionHandling from "@/hooks/useUserInteractionHandling";

export default function WordPressIntegrationStep() {
  const { state, setState } = useOnboarding();
  const { handleFrequencyChange } = useOnboardingSetupAndInitialization();
  const { handleUpdateUrlParams, handleDayToggle, handleContinueToAuth } =
    useUserInteractionHandling();

  return (
    <motion.div
      key="step-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{
        duration: 0.5,
        ease: "easeInOut",
        opacity: { duration: 0.3 },
      }}
      className="w-full py-4"
    >
      <div className="w-full mx-auto max-w-3xl">
        <h2 className="text-2xl font-bold mb-4 text-center">Plan your growth 🌱</h2>

        <p className="text-muted-foreground text-center mb-8">
          How often would you like to post content? - This will make sure the gardener
          takes responsibility for generating ideas and content for you weekly.
        </p>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Publishing frequency</CardTitle>
            <CardDescription>
              How often would you like fresh content for your audience?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">1x per week</span>
                <span className="text-sm font-medium">10x per week</span>
              </div>
              <div className="relative">
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={state.postingFrequency}
                  onChange={(e) => handleFrequencyChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <div className="text-2xl font-semibold">{state.postingFrequency}x</div>
                  <div className="text-sm text-muted-foreground">per week</div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {state.postingFrequency === 1
                    ? "Perfect for maintaining a steady presence"
                    : state.postingFrequency <= 3
                    ? "Ideal for growing your audience consistently"
                    : state.postingFrequency <= 5
                    ? "Great for high engagement and SEO impact"
                    : "Maximum impact and authority building"}
                </div>
              </div>
              <div className="text-xs text-muted-foreground text-center">
                {state.postingFrequency <= 1
                  ? "Hobby Plan for €15/month - Starting with a free trial"
                  : state.postingFrequency <= 3
                  ? "Pro Plan - Starting at €49/month - Starting with a free trial"
                  : state.postingFrequency <= 5
                  ? "Pro Plan (€49/month) + Article Package (10 articles for €20) - Starting with a free trial"
                  : state.postingFrequency <= 7
                  ? "Pro Plan (€49/month) + Article Packages (20 articles for €40) - Starting with a free trial"
                  : "Pro Plan (€49/month) + Article Packages (30 articles for €60) - Starting with a free trial"}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Publishing days</CardTitle>
            <CardDescription>
              When would you like to publish your content?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {["monday", "tuesday", "wednesday", "thursday", "friday"].map((day, index) => (
                  <div key={`1-${day}-${index}`} className="flex flex-col items-center gap-2">
                    <Toggle
                      pressed={state.postingDays.includes(day)}
                      onPressedChange={() => handleDayToggle(day)}
                      className="capitalize w-full text-sm"
                    >
                      {day}
                    </Toggle>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          const currentCount = state.postingDays.filter(
                            (d) => d === day
                          ).length;
                          if (currentCount > 0) {
                            setState((prev) => ({
                              ...prev,
                              postingDays: prev.postingDays.filter(
                                (d, i) =>
                                  !(d === day && i === prev.postingDays.lastIndexOf(day))
                              ),
                            }));
                          }
                        }}
                      >
                        -
                      </Button>
                      <span className="text-sm min-w-[1.5rem] text-center">
                        {state.postingDays.filter((d) => d === day).length}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          const currentCount = state.postingDays.filter(
                            (d) => d === day
                          ).length;
                          const totalPosts = state.postingDays.length;
                          if (totalPosts < state.postingFrequency) {
                            setState((prev) => ({
                              ...prev,
                              postingDays: [...prev.postingDays, day],
                            }));
                          }
                        }}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {state.postingDays.length > 0 && (
                <div className="text-sm text-muted-foreground mt-4">
                  <p className="font-medium">Your publishing schedule:</p>
                  <div className="mt-2 space-y-2">
                    {Array.from(new Set(state.postingDays)).map((day, index) => {
                      const postsOnDay = state.postingDays.filter(
                        (d) => d === day
                      ).length;
                      return (
                        <div
                          key={`publishing-${day}-${index}`}
                          className="flex flex-col sm:flex-row sm:items-center gap-2"
                        >
                          <span className="capitalize font-medium min-w-[100px]">
                            {day}:
                          </span>
                          <div className="flex gap-1">
                            {Array.from({ length: postsOnDay }).map((_, i) => (
                              <div key={`dsf2-${i}`} className="w-2 h-2 rounded-full bg-primary" />
                            ))}
                          </div>
                          <span className="sm:ml-2">
                            {postsOnDay} post{postsOnDay !== 1 ? "s" : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-xs">
                    Total: {state.postingDays.length} post
                    {state.postingDays.length !== 1 ? "s" : ""} per week
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setState((prev) => ({ ...prev, step: 3 }));
              handleUpdateUrlParams({ step: "post-draft" });
            }}
          >
            Back
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              console.log("Skipping WordPress step → moving to preferences (step 5)");
              // Proceed to preferences step even if WordPress is skipped
              setState((prev) => ({ ...prev, step: 5 }));
              handleUpdateUrlParams({ step: "publish" });
            }}
          >
            Skip
          </Button>
          <Button
            onClick={() => {
              // After saving scheduling settings, proceed to preferences step
              handleContinueToAuth();
              setTimeout(() => {
                setState((prev) => ({ ...prev, step: 5 }));
                handleUpdateUrlParams({ step: "publish" });
              }, 0);
            }}
            disabled={state.postingDays.length !== state.postingFrequency}
          >
            Continue <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
